import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  BarChart3,
  Boxes,
  Building2,
  ChevronRight,
  Contact,
  FileText,
  Frame,
  Globe,
  UserPlus,
  Headset,
  KanbanSquare,
  LayoutDashboard,
  MonitorSmartphone,
  Network,
  ReceiptText,
  ServerCog,
  CircleDollarSign,
  Settings2,
  Sparkles,
  Store,
  Tags,
  Users,
  Wallet,
  Wrench,
  Calculator,
  Scale,
} from 'lucide-react';
import { useStaffSupport } from '../../support/staff/staff-support.store';
import { useAdminNav } from '../shell/admin-nav.store';

import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarMenuBadge,
} from '@/components/ui/sidebar';

/**
 * The super admin's navigation.
 *
 * Deliberately the same shape as the vendor menu — one declarative tree
 * rendered by shadcn's sidebar primitives — because both render inside the
 * same shell, and two hand-styled navigations is how they drifted apart.
 * See `vendor.sidebar-menu.tsx`.
 */

interface NavItem {
  route: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** A child is lit on its exact path unless `exact: false` (then its sub-pages light it too). */
  children?: Array<{ route: string; label: string; exact?: boolean }>;
  /** Count shown at the end of the row (hidden at 0). */
  badge?: number;
  /** Lit only on this exact path (a parent path would stay lit on its children). */
  exact?: boolean;
}

interface NavSection {
  /** Omitted for the first group, which needs no heading. */
  label?: string;
  items: NavItem[];
}

export const SuperAdminMenu = (_props: { isDrawerOpen?: boolean; isCollapsed?: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const supportUnread = useStaffSupport((s) => s.counts?.unread ?? 0);
  const { quotesNew, invoicesOverdue, jobsStuck, costsAttention, signupsPending, crmAllowed } = useAdminNav();

  const isActive = React.useCallback(
    (route: string, exact = false) =>
      exact
        ? location.pathname === route
        : location.pathname === route || location.pathname.startsWith(`${route}/`),
    [location.pathname],
  );

  // Grouped by the job at hand: who our customers are, selling to them,
  // answering them, the shared catalogue, getting paid, keeping it running.
  // Badges are the counts that want a person (see AdminNavSync).
  const sections: NavSection[] = [
    {
      items: [
        { route: '/dashboard', label: t('ADMIN_SHELL.NAV.OVERVIEW'), icon: LayoutDashboard },
        // Everything the platform flagged for staff, and each person's email choices.
        { route: '/notifications', label: t('ARDI.NAV.NOTIFICATIONS'), icon: Bell },
      ],
    },
    {
      label: t('ADMIN_SHELL.NAV.SECTION_CUSTOMERS'),
      items: [
        { route: '/vendors', label: t('ADMIN_SHELL.NAV.VENDORS'), icon: Store },
        // Self-serve signups; the count is the accounts waiting for a person to approve them.
        { route: '/signups', label: t('ADMIN_SIGNUPS.NAV'), icon: UserPlus, badge: signupsPending },
        // How vendors use the backoffice: features, AI in $ and credits, per vendor.
        { route: '/vendor-usage', label: t('ADMIN_USAGE.NAV'), icon: BarChart3 },
        { route: '/users', label: t('ADMIN_SHELL.NAV.USERS'), icon: Users },
        // Internal sales CRM: only for the admins on its allowlist (CRM_ALLOWED_EMAILS).
        ...(crmAllowed ? [{ route: '/crm', label: t('ADMIN_SHELL.NAV.CRM'), icon: Contact }] : []),
      ],
    },
    {
      label: t('ADMIN_SHELL.NAV.SECTION_SALES'),
      items: [
        { route: '/quote-requests', label: t('ADMIN_SHELL.NAV.QUOTES'), icon: FileText, badge: quotesNew },
        { route: '/demo-sites', label: t('SUPER_ADMIN.DEMO_SITES.NAV'), icon: Globe },
        { route: '/demo-generator', label: t('SUPER_ADMIN.DEMO_GENERATOR.NAV'), icon: Sparkles },
      ],
    },
    {
      label: t('ADMIN_SHELL.NAV.SECTION_SUPPORT'),
      items: [
        { route: '/support', label: t('SUPPORT.STAFF.INBOX'), icon: Headset, badge: supportUnread, exact: true },
        { route: '/support/board', label: t('SUPPORT.STAFF.BOARD'), icon: KanbanSquare },
      ],
    },
    {
      label: t('ADMIN_SHELL.NAV.SECTION_CATALOG'),
      items: [
        { route: '/spaces', label: t('ADMIN_SHELL.NAV.SPACES'), icon: Building2 },
        { route: '/space-manager', label: t('ADMIN_SHELL.NAV.SPACE_MANAGER'), icon: Frame },
        { route: '/product-families', label: t('ADMIN_SHELL.NAV.FAMILIES'), icon: Boxes },
        { route: '/product-types', label: t('ADMIN_SHELL.NAV.TYPES'), icon: Tags },
        // The Ardealy Network: brands, their products, and the matching review queue.
        {
          route: '/network',
          label: t('ADMIN_NETWORK.NAV'),
          icon: Network,
          children: [
            { route: '/network', label: t('ADMIN_NETWORK.NAV_OVERVIEW') },
            { route: '/network/brands', label: t('ADMIN_NETWORK.NAV_BRANDS'), exact: false },
            { route: '/network/review', label: t('ADMIN_NETWORK.NAV_REVIEW') },
          ],
        },
      ],
    },
    {
      label: t('ADMIN_SHELL.NAV.SECTION_BILLING'),
      items: [
        { route: '/pricing', label: t('PRICING.ADMIN.NAV'), icon: Calculator },
        { route: '/plans', label: t('ADMIN_SHELL.NAV.PLANS'), icon: Wallet },
        { route: '/wire-invoices', label: t('ADMIN_SHELL.NAV.INVOICES'), icon: ReceiptText, badge: invoicesOverdue },
        { route: '/wire-settings', label: t('ADMIN_SHELL.NAV.BILLING_SETTINGS'), icon: Settings2 },
      ],
    },
    {
      label: t('ADMIN_SHELL.NAV.SECTION_PLATFORM'),
      items: [
        { route: '/costs', label: t('ADMIN_COSTS.NAV'), icon: CircleDollarSign, badge: costsAttention },
        { route: '/unit-economics', label: t('R2R.NAV.UNIT_ECONOMICS'), icon: Scale },
        { route: '/queue-monitor', label: t('ADMIN_SHELL.NAV.QUEUES'), icon: ServerCog, badge: jobsStuck },
        { route: '/team-devices', label: t('TEAM.NAV'), icon: MonitorSmartphone },
        // Kept out of the way, folded at the bottom: internal makers, not daily pages.
        { route: '/tools', label: t('ADMIN_TOOLS.NAV'), icon: Wrench, children: [{ route: '/tools/3d-generator', label: t('ADMIN_TOOLS.NAV_3D') }] },
      ],
    },
  ];

  const renderItem = (item: NavItem) => {
    const active = isActive(item.route, item.exact);
    const Icon = item.icon;

    const buttonClass = cn(
      // Same grammar as the vendor menu: grey at rest, black on hover or when
      // current, word and glyph together.
      'h-8 gap-2.5 text-sm font-normal',
      'data-[active=true]:font-medium',
    );

    if (!item.children?.length) {
      return (
        <SidebarMenuItem key={item.route}>
          <SidebarMenuButton
            isActive={active}
            tooltip={item.label}
            onClick={() => navigate(item.route)}
            className={buttonClass}
          >
            <Icon className="size-4" />
            <span>{item.label}</span>
          </SidebarMenuButton>
          {!!item.badge && <SidebarMenuBadge className="tabular-nums">{item.badge > 99 ? '99+' : item.badge}</SidebarMenuBadge>}
        </SidebarMenuItem>
      );
    }

    return (
      <Collapsible key={item.route} asChild defaultOpen={active} className="group/collapsible">
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton isActive={active} tooltip={item.label} className={buttonClass}>
              <Icon className="size-4" />
              <span>{item.label}</span>
              <ChevronRight className="ms-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.children.map((child) => (
                <SidebarMenuSubItem key={child.route}>
                  <SidebarMenuSubButton
                    // Exact, or "All Vendors" would stay lit while you are on
                    // "Create Vendor" — every child sits under its parent path.
                    isActive={isActive(child.route, child.exact ?? true)}
                    onClick={() => navigate(child.route)}
                    className="cursor-pointer text-sm"
                  >
                    <span>{child.label}</span>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  };

  return (
    <>
      {sections.map((section, index) => (
        <SidebarGroup
          key={section.label ?? `group-${index}`}
          className={cn('py-0', index === 0 ? 'pt-0' : 'pt-5')}
        >
          {section.label && (
            <SidebarGroupLabel className="h-6 px-2 text-xs leading-4 font-normal tracking-normal">
              {section.label}
            </SidebarGroupLabel>
          )}
          <SidebarMenu className="gap-0">{section.items.map(renderItem)}</SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  );
};
